/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';

import { DialogRef, DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Vehicle } from 'rest-client';
import { AuthenticationService } from 'rest-client';
import { Observable } from 'rxjs/internal/Observable';

import { SetVehicleAction } from '@app/modules/core/states/vehicle-state/vehicle.actions';
import { VehicleState } from '@app/modules/core/states/vehicle-state/vehicle.state';
import { DIALOG_CONFIG } from '@app/modules/core/utils/dialog.config';
import { GoogleTagManagerService } from '@core/services/analytics/google-tag-manager.service';
import { VehicleData } from '@shared/models/vehicle-data.model';
import { RequestedCarServicesData } from '@shared/utils/vehicle-status-checker';

import { VehicleReturned } from '../../models';
import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';

interface ErrorReason {
  reason: string;
  error: string;
}
@UntilDestroy()
@Component({
  selector: 'bkg-vehicle-dialog',
  templateUrl: './vehicle-dialog.component.html',
})
export class VehicleDialogComponent implements OnInit {
  @Select(VehicleState.vehicleData)
  requestedVehicleData$: Observable<VehicleData>;
  errorMessage: string | null;
  vehicleData: VehicleData;
  requestedCSData: RequestedCarServicesData;

  isLogged: boolean;
  clickNewPlate: boolean;
  clickUmcSelectors: boolean;
  clickNotFoundForm: boolean;
  createNewVehicle: boolean;
  modifyVehicle: boolean;
  plateNotFounded: string;
  notFoundedVehicleID: number;
  addMoreInfoLicense: boolean;
  addMoreInfoUmc: boolean;
  addMoreInfoClient: boolean;
  newVehicleFeedback: Vehicle;
  agentVehicle: Vehicle;
  clientId: number;
  modifiedVehicle: Vehicle;

  constructor(
    public ref: DialogRef,
    private store: Store,
    private authService: AuthenticationService,
    private googleTagManagerService: GoogleTagManagerService,
    private translateService: TranslateService,
    private dialog: DialogService
  ) {}

  ngOnInit(): void {
    this.requestedCSData = this.ref.data
      ?.requestedCSData as RequestedCarServicesData;
    this.createNewVehicle = this.ref.data?.createNewVehicle as boolean;
    this.modifyVehicle = this.ref.data?.isModifyVehicle as boolean;
    this.clientId = this.ref.data?.clientId as number;
    this.agentVehicle = this.ref.data?.agentVehicle as Vehicle;
    this.modifiedVehicle = this.ref.data?.modifiedVehicle as Vehicle;
    this.isLogged =
      this.authService.isTokenValid() && this.authService.isClient();
    this.subscribeToRequestedVehicleData();
  }

  get moreInfoShowing(): boolean {
    return (
      this.addMoreInfoLicense || this.addMoreInfoUmc || this.addMoreInfoClient
    );
  }

  private subscribeToRequestedVehicleData(): void {
    this.requestedVehicleData$
      .pipe(untilDestroyed(this))
      .subscribe(
        (requestedVehicleData: VehicleData) =>
          (this.vehicleData = requestedVehicleData)
      );
  }

  onCloseDialog(vehicle?: Vehicle): void {
    if (vehicle) {
      this.onSelectedVehicle({ vehicle: vehicle, type: 'close' });
    }
    if (this.newVehicleFeedback) {
      this.openFeedbackDialog(this.newVehicleFeedback);
    }
    this.ref.close(true);
  }

  onClickClose(): void {
    if (this.newVehicleFeedback) {
      this.openFeedbackDialog(this.newVehicleFeedback);
    }
    this.ref.close(false);
  }

  onCloseBanner(): void {
    this.errorMessage = null;
  }

  onError(error: HttpErrorResponse): void {
    const errorTranslation = error?.error as ErrorReason;
    this.errorMessage = errorTranslation?.reason || 'Error';
    this.googleTagManagerService.pushOnDataLayer({
      event: 'error',
      error: errorTranslation?.error,
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'error',
      error: errorTranslation?.error,
    });
  }

  onClickAddNewVehicle = (value: boolean): void => {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'anade-nuevo-link',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'anade-nuevo-link',
    });
    this.clickNewPlate = value;
  };

  onClickShowUmcSelectors = (value: boolean): void => {
    this.clickNewPlate = !value;
    this.clickUmcSelectors = value;
  };

  onPlateNotFoundedReturn = (value: string): void => {
    this.plateNotFounded = value;
  };

  onVehicleNotFoundedReturn = (value: number): void => {
    this.notFoundedVehicleID = value;
  };

  onClickNotFoundCarOption = (value: boolean): void => {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'no-encuentro-coche-link',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'no-encuentro-coche-link',
    });
    this.clickUmcSelectors = !value;
    this.clickNotFoundForm = value;
  };

     onSelectedVehicle(returnedVehicle: VehicleReturned): void {
    if (
      returnedVehicle.type === 'license' ||
      returnedVehicle.type === 'umcSelector' ||
      returnedVehicle.type === 'umcData'
    ) {
      this.newVehicleFeedback = returnedVehicle.vehicle;
    }
    const { vehicle, type } = returnedVehicle;
    const {
      id,
      vehicle_information,
      km,
      km_timestamp: kmTimestamp,
      workshop_type,
    } = vehicle;

    const vehicleData = {} as VehicleData;

    vehicleData.id = id;
    vehicleData.km = km;
    vehicleData.kmTimestamp = kmTimestamp;
    vehicleData.motorCode =
    vehicle_information?.motor_applicabilitycode ?? null;
    vehicleData.bodyCode = vehicle_information?.body_description ?? null;
    vehicleData.bodyDesc = vehicle_information?.model_description ?? null;
    vehicleData.umc = vehicle_information?.umc ?? null;
    vehicleData.umcData = vehicle_information?.umc_data ?? null;
    vehicleData.workshopType = +workshop_type;
    vehicleData.makeCode = vehicle_information?.make_code ?? null;
    vehicleData.propulsionCode = vehicle_information?.propulsion_code ?? null;

      if (this.agentVehicle?.id) {
        vehicle.plate = this.agentVehicle.plate;
      }
      this.store.dispatch(new SetVehicleAction(vehicle, vehicleData));

      if (this.createNewVehicle || (this.modifyVehicle && this.vehicleData.umc)) {
        if (this.newVehicleFeedback) {
          this.openFeedbackDialog(this.newVehicleFeedback);
        }
        this.ref.close(true);
      } else {
        if (type === 'license') {
          this.addMoreInfoLicense = true;
        } else if (type === 'umcSelector') {
          this.addMoreInfoUmc = true;
        } else if (type === 'clientVehicle') {
          this.addMoreInfoClient = true;
        }
      }
  }

  get selectClientCarView(): boolean {
    return (
      (this.isLogged &&
        !this.vehicleData.id &&
        !this.clickNewPlate &&
        !this.clickUmcSelectors &&
        !this.clickNotFoundForm &&
        !this.createNewVehicle &&
        !this.plateNotFounded) ||
      this.addMoreInfoClient ||
      (this.clientId &&
        this.clientId > 1 &&
        !this.vehicleData.id &&
        !this.clickNewPlate &&
        !this.clickUmcSelectors &&
        !this.clickNotFoundForm &&
        !this.createNewVehicle &&
        !this.plateNotFounded)
    );
  }

  get addLicensePlateView(): boolean {
    return (
      (!this.isLogged &&
        !this.clientId &&
        !this.vehicleData.id &&
        !this.clickUmcSelectors &&
        !this.clickNotFoundForm) ||
      this.clickNewPlate ||
      (this.createNewVehicle &&
        !this.vehicleData.id &&
        !this.clickUmcSelectors &&
        !this.clickNotFoundForm) ||
      this.addMoreInfoLicense ||
      (this.clientId &&
        this.clientId === 1 &&
        !this.vehicleData.id &&
        !this.clickUmcSelectors &&
        !this.clickNotFoundForm)
    );
  }

  get umcSelectorView(): boolean {
    return (
      (this.vehicleData.id && !this.vehicleData.umc && !this.vehicleData.umcData)
      || (this.clickUmcSelectors && !this.vehicleData.umc)
      || this.addMoreInfoUmc
    );
  }

  get umcDataSelectorsView(): boolean {
    return (
      (this.vehicleData.id && !this.vehicleData.umc && !!this.vehicleData.umcData)
      || (this.vehicleData.id && this.vehicleData.umc && this.vehicleData.propulsionCode === null)
      || (this.vehicleData.id && this.vehicleData.umc && !this.vehicleData.bodyDesc)

    );
  }

  get requestedDataView(): boolean {
    return (
      this.vehicleData.umc && !this.createNewVehicle && !this.modifyVehicle && this.vehicleData.propulsionCode !== null
    );
  }

  get notFoundVehicleFormView(): boolean {
    return this.clickNotFoundForm;
  }

  get showReturnArrow(): boolean {
    return this.canShowReturnArrow;
  }

  get canShowReturnArrow(): boolean {
    return (
      this.clickNotFoundForm ||
      (this.clickUmcSelectors && !this.vehicleData.umc) ||
      (this.clickNewPlate && this.isLogged && !this.createNewVehicle)
    );
  }

  returnArrowClick = (): void => {
    if (this.notFoundVehicleFormView) {
      this.clickNotFoundForm = false;
      this.clickUmcSelectors = true;
    } else if (this.umcSelectorView) {
      this.clickUmcSelectors = false;
      this.clickNewPlate = true;
    } else if (this.addLicensePlateView && this.isLogged) {
      this.clickNewPlate = false;
      this.plateNotFounded = '';
    }
  };

  private openFeedbackDialog(vehicle: Vehicle): void {
    const plate = vehicle?.plate ? vehicle.plate : '';
    let model =
      vehicle?.vehicle_information &&
      vehicle?.vehicle_information.make_description
        ? vehicle?.vehicle_information.make_description +
          ' ' +
          vehicle?.vehicle_information.model_description
        : '';
    if (model === '') {
      model = vehicle.type;
    }
    let modalTitle = this.translateService.instant(
      'sharedModule.bkgFeedbackDialog.vehicleTitle'
    ) as string;
    if (this.modifiedVehicle) {
      modalTitle = this.translateService.instant(
        'sharedModule.bkgFeedbackDialog.vehicleTitleEdit'
      ) as string;
    }
    this.dialog.open(FeedbackDialogComponent, {
      ...DIALOG_CONFIG,
      size: 'md',
      enableClose: false,
      data: {
        icon: 'assets/images/icons/ok-icon.svg',
        title: modalTitle,
        body: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleBody',
          { plate: plate ?? '', model: model ?? '' }
        ) as string,
      },
    });
  }
}
